import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {SemanticICONS} from "semantic-ui-react";
import {ConditionalLinkConfiguration, LinkConfiguration} from "../core/config/link-configuration";
import { SimpleLineSymbolConfiguration} from "../core/config/symbol-configuration";
import {LegendConfiguration as EsriLegendConfiguration} from "../core/config/legend-configuration";
import {MessageConfiguration} from "../core/config/message-configuration";

export enum LegendStyleType {
    List = "list",
    Card = "card"
}

export enum LegendLayoutType {
    Stack = "stack",
    SideBySide = "side-by-side"
}

export enum LegendItemType {
    Icon = "icon",
    Path = "path",
    PatchRect = "patch-rect",
    CircleHalfTop = "circle-half-top",
    CircleHalfBottom = "circle-half-bottom",
    Circle = "circle"
}

@JsonObject()
export class LegendStyleConfiguration {

    /**
     * Type of style
     */
    @JsonProperty()
    declare type: LegendStyleType;

    /**
     * Layout
     */
    @JsonProperty()
    declare layout: LegendLayoutType;

    constructor() {}
}

@JsonObject()
export class LegendItemConfiguration {

    /**
     * Item ID
     */
    @JsonProperty()
    declare id: string;

    /**
     * Item type. The type defines how th item is rendered.
     */
    @JsonProperty()
    declare type: LegendItemType;

    /**
     * Class name
     */
    @JsonProperty()
    declare class: string;

    /**
     * Description of the item
     */
    @JsonProperty()
    declare description: string;

    /**
     * Color as HEX string
     */
    @JsonProperty()
    declare color: string;

    /**
     * URL (for type icon only)
     */
    @JsonProperty()
    declare url: string;

    /**
     * Semantic UI Icon Name
     * See https://react.semantic-ui.com/elements/icon/
     */
    @JsonProperty()
    declare iconName: SemanticICONS;

    /**
     * Icon as SVG path
     * Set LegendItemType.Path to use it
     */
    @JsonProperty()
    declare svgPath: string;

    /**
     * The angle (in °) of the patch/icon
     * Only applied to svgPath
     */
    @JsonProperty()
    declare angle: number;

    /**
     * The outline color
     */
    @JsonProperty()
    declare outline: SimpleLineSymbolConfiguration;

    constructor() {}
}

@JsonObject()
export class LegendItemGroupConfiguration {

    /**
     * Group ID
     */
    @JsonProperty()
    declare id: string;

    /**
     * Group title
     */
    @JsonProperty()
    declare title: string;

    /**
     * Belongs to this topics
     */
    @JsonProperty()
    declare topics: string[];

    /**
     * Link to external web page
     * @example: page with additional information
     */
    @JsonProperty()
    declare link: LinkConfiguration;

    /**
     * Legend items
     */
    @JsonProperty()
    declare items: LegendItemConfiguration[];

    constructor() {}
}

@JsonObject()
export class LegendLayerInfoConfiguration {

    /**
     * Layer ID
     */
    @JsonProperty()
    declare id: string;

    /**
     * Title for layer legend
     */
    @JsonProperty()
    declare title: string;

    /**
     * Group of items. To divide legend of a layer in sub sections.
     * E.g. to represent filters.
     */
    @JsonProperty()
    declare itemGroups: LegendItemGroupConfiguration[];

    constructor() {}
}

@JsonObject()
export class LegendConfiguration {
    /**
     * Use Esri legend or a custom
     */
    @JsonProperty()
    declare useEsri: boolean;

    /**
     * Legend definitions for layers
     */
    @JsonProperty()
    declare esriConfig: EsriLegendConfiguration | undefined;
    /**
     * Link zu PDF Version
     */
    @JsonProperty()
    declare links?: ConditionalLinkConfiguration[];
    /**
     * Legend position
     * Possible Values: "panel": seperat legend panel
     *                  "toc":   in TOC panel
     *                  "layer": in TOC after the corresponding layer
     */
    @JsonProperty()
    declare position: "panel" | "toc" | "layer";

    /**
     * Initial width of popup window
     */
    @JsonProperty()
    declare width: number;

    /**
     * Initial height of popup window
     */
    @JsonProperty()
    declare height: number;

    /**
     * Minimum height in fixed position
     */
    @JsonProperty()
    declare fixedMinHeight: number;

    /**
     * Heading title
     */
    @JsonProperty()
    declare title: string;

    /**
     * Style of legend
     */
    @JsonProperty()
    declare style: LegendStyleConfiguration;

    /**
     * Legend definitions for layers
     */
    @JsonProperty()
    declare layerInfos: LegendLayerInfoConfiguration[];
    /**
     * Metadata for Legend
     */
    @JsonProperty()
    declare preContent?: MessageConfiguration[];
    /**
     * Metadata for Legend
     */
    @JsonProperty()
    declare postContent?: MessageConfiguration[];

    constructor() {}
}
